import React, { useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { T, springs } from '../../lib/tokens';

/**
 * Liquid Glass Material Component
 *
 * Features:
 * - Mouse-tracking specular highlight
 * - Depth-aware blur (depth 0-3)
 * - Content tinting via tint prop
 * - Spring hover animation
 */
const depthStyles = {
  0: {
    backdropFilter: 'blur(20px) saturate(190%)',
    WebkitBackdropFilter: 'blur(20px) saturate(190%)',
    background: 'rgba(255,255,255,0.42)',
  },
  1: {
    backdropFilter: 'blur(28px) saturate(190%)',
    WebkitBackdropFilter: 'blur(28px) saturate(190%)',
    background: 'rgba(255,255,255,0.58)',
  },
  2: {
    backdropFilter: 'blur(40px) saturate(210%)',
    WebkitBackdropFilter: 'blur(40px) saturate(210%)',
    background: 'rgba(255,255,255,0.72)',
  },
  3: {
    backdropFilter: 'blur(56px) saturate(220%)',
    WebkitBackdropFilter: 'blur(56px) saturate(220%)',
    background: 'rgba(255,255,255,0.84)',
  },
};

const shadowByDepth = {
  0: '0 4px 14px rgba(60,40,90,0.06)',
  1: '0 14px 44px rgba(60,40,90,0.10), 0 3px 10px rgba(60,40,90,0.05)',
  2: '0 28px 80px rgba(60,40,90,0.14), 0 6px 20px rgba(60,40,90,0.08)',
  3: '0 40px 120px rgba(60,40,90,0.18), 0 12px 36px rgba(60,40,90,0.10)',
};

const tintGradients = {
  amber:  'linear-gradient(135deg, rgba(212,146,30,0.06), transparent 60%)',
  blue:   'linear-gradient(135deg, rgba(74,158,204,0.06), transparent 60%)',
  violet: 'linear-gradient(135deg, rgba(139,114,200,0.06), transparent 60%)',
  green:  'linear-gradient(135deg, rgba(90,158,120,0.06), transparent 60%)',
  red:    'linear-gradient(135deg, rgba(224,88,88,0.06), transparent 60%)',
};

export const Glass = ({
  children,
  style,
  onClick,
  hover = true,
  depth = 1,
  tint,
  specular = true,
  className,
}) => {
  const ref = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!specular || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    ref.current.style.setProperty('--mouse-x', `${x}%`);
    ref.current.style.setProperty('--mouse-y', `${y}%`);
  }, [specular]);

  const ds = depthStyles[depth] || depthStyles[1];

  return (
    <motion.div
      ref={ref}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      className={className}
      whileHover={hover ? {
        y: -2,
        boxShadow: shadowByDepth[Math.min(depth + 1, 3)],
        background: 'rgba(255,255,255,0.78)',
      } : undefined}
      whileTap={onClick ? { scale: 0.995 } : undefined}
      transition={springs.snappy}
      style={{
        ...ds,
        border: `1px solid ${T.glassBorder}`,
        borderRadius: 24,
        boxShadow: `${shadowByDepth[depth]}, inset 0 1px 0 rgba(255,255,255,0.55), inset 0 0 0 1px rgba(255,255,255,0.10)`,
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* Top edge highlight — gives the "etched" iOS card feel */}
      <div
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, height: 1,
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.85) 35%, rgba(255,255,255,0.85) 65%, transparent)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />

      {/* Specular highlight */}
      {specular && (
        <div
          style={{
            position: 'absolute',
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 40%, transparent 70%)',
            filter: 'blur(80px)',
            pointerEvents: 'none',
            transform: 'translate(calc(var(--mouse-x, 50%) - 50%), calc(var(--mouse-y, 50%) - 50%))',
            transition: 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1)',
            zIndex: 0,
            top: 0,
            left: 0,
          }}
        />
      )}

      {/* Content tint overlay */}
      {tint && tintGradients[tint] && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            background: tintGradients[tint],
            mixBlendMode: 'soft-light',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </motion.div>
  );
};

/**
 * GlassCard — Glass + padding + optional section header
 */
export const GlassCard = ({
  children,
  title,
  icon,
  iconBg,
  iconColor,
  subtitle,
  right,
  style,
  depth = 1,
  tint,
  hover = false,
  onClick,
}) => (
  <Glass depth={depth} tint={tint} hover={hover} onClick={onClick} style={{ padding: 28, ...style }}>
    {(title || icon) && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        {icon && (
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: iconBg || T.amberLight,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {React.createElement(icon, { size: 14, color: iconColor || T.amber })}
          </div>
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: T.textMuted }}>{subtitle}</div>}
        </div>
        {right}
      </div>
    )}
    {children}
  </Glass>
);
